import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

interface Address {
  id: string;
  text: string;
  coordinates: [number, number];
}

interface AddressFormProps {
  onAddressesChange: (addresses: Address[]) => void;
  onOptimizeRoute: () => void;
}

interface TourStep {
  target: string;
  content: string;
}

const LoadingOverlay: React.FC<{ spinner: boolean; active: boolean; text: string }> = ({ spinner, active, text }) => {
  if (!active) return null;
  
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-xl p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
        <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
          <div className="loading-progress-bar h-full bg-blue-500 rounded-full"></div>
        </div>
        <p className="text-lg text-white font-medium">{text}</p>
      </div>
    </div>
  );
};

const AddressForm: React.FC<AddressFormProps> = ({ onAddressesChange, onOptimizeRoute }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [currentAddress, setCurrentAddress] = useState<Address | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [hasUserLocation, setHasUserLocation] = useState(false);
  const [locationPulse, setLocationPulse] = useState(true);
  const [locationActive, setLocationActive] = useState(false);
  const [userLocationText, setUserLocationText] = useState<string | null>(null);
  const containerRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const geocoderRefs = useRef<{ [key: string]: MapboxGeocoder | null }>({});

  const tourSteps: TourStep[] = [
    {
      target: '.location-input-section',
      content: '👋 Welcome! Start by entering your current location or use the location button to automatically detect it.'
    },
    {
      target: '.showing-address-input',
      content: '📍 Now enter the address of a property you want to show and click the + button to add it. You need to add at least 2 properties. And can add up to 10 properties.'
    },
    {
      target: 'body',
      content: '🎯 Perfect! Now that you have added at least 2 properties (max 10), click on optimize route button below and see the best path to visit all properties.'
    }
  ];

  // Check if tooltips have been shown before
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenTour');
    if (!hasSeenTour) {
      setShowTour(true);
      setCurrentStep(0);
      console.log('Showing initial tour tooltip');
    }
  }, []);

  // Handle location added
  const handleLocationAdded = () => {
    const hasSeenLocationTooltip = localStorage.getItem('hasSeenLocationTooltip');
    if (!hasSeenLocationTooltip) {
      setCurrentStep(1);
      setShowTour(true);
      localStorage.setItem('hasSeenLocationTooltip', 'true');
      console.log('Showing location tooltip');
    }
  };

  // Handle address added
  const handleAddressAdded = (totalAddresses: number) => {
    const nonUserLocations = totalAddresses - (addresses.some(addr => addr.id === 'user-location') ? 1 : 0);
    if (nonUserLocations === 2) {
      const hasSeenOptimizeTooltip = localStorage.getItem('hasSeenOptimizeTooltip');
      if (!hasSeenOptimizeTooltip) {
        setCurrentStep(2);
        setShowTour(true);
        localStorage.setItem('hasSeenOptimizeTooltip', 'true');
        console.log('Showing optimize tooltip');
      }
    }
  };

  // For testing - let's clear the localStorage flags
  useEffect(() => {
    localStorage.removeItem('hasSeenTour');
    localStorage.removeItem('hasSeenLocationTooltip');
    localStorage.removeItem('hasSeenOptimizeTooltip');
  }, []);

  // Effect to initialize geocoders
  useEffect(() => {
    const token = process.env.REACT_APP_MAPBOX_TOKEN || '';
    if (!mapboxgl.accessToken) {
      mapboxgl.accessToken = token;
    }

    // Store the current proximity for showingGeocoder
    let currentProximity: { longitude: number; latitude: number } | undefined = undefined;
    // If user location is already set, use it for proximity
    const userLoc = addresses.find(addr => addr.id === 'user-location');
    if (userLoc) {
      currentProximity = { longitude: userLoc.coordinates[0], latitude: userLoc.coordinates[1] };
    }

    const userLocationGeocoder = new MapboxGeocoder({
      accessToken: token,
      countries: 'us',
      language: 'en',
      placeholder: 'Enter your current location (US)',
      marker: false,
    });

    const showingGeocoder = new MapboxGeocoder({
      accessToken: token,
      countries: 'us',
      language: 'en',
      placeholder: 'Enter a property address to show (US)',
      marker: false,
      proximity: currentProximity,
    });

    if (containerRefs.current['user-location']) {
      userLocationGeocoder.addTo(containerRefs.current['user-location']);
      geocoderRefs.current['user-location'] = userLocationGeocoder;
    }

    // Only add the property address geocoder if the container exists (i.e., input is visible)
    if (hasUserLocation && containerRefs.current['showing']) {
      showingGeocoder.addTo(containerRefs.current['showing']);
      geocoderRefs.current['showing'] = showingGeocoder;
    }

    userLocationGeocoder.on('result', (e) => {
      console.log('Location selected:', e.result.place_name);
      const coords = e.result.center;
      setAddresses(prev => {
        const newAddresses = prev.filter(addr => addr.id !== 'user-location');
        return [{
          id: 'user-location',
          text: e.result.place_name,
          coordinates: coords,
        }, ...newAddresses];
      });
      setHasUserLocation(true);
      setLocationPulse(false);
      setLocationActive(true);
      setUserLocationText(e.result.place_name);
      handleLocationAdded();
      if (geocoderRefs.current['showing'] && coords) {
        geocoderRefs.current['showing'].setProximity({ longitude: coords[0], latitude: coords[1] });
      }
    });

    showingGeocoder.on('result', (e) => {
      const coords = e.result.center;
      setCurrentAddress({
        id: Date.now().toString(),
        text: e.result.place_name,
        coordinates: coords,
      });
    });

    return () => {
      Object.values(geocoderRefs.current).forEach(geocoder => {
        if (geocoder) {
          geocoder.onRemove();
        }
      });
    };
  }, [addresses.find(addr => addr.id === 'user-location')]);

  // Effect to monitor user location status
  useEffect(() => {
    if (hasUserLocation) {
      console.log('User location set, showing property input');
    }
  }, [hasUserLocation]);

  const handleAddAddress = () => {
    if (currentAddress) {
      setAddresses(prev => {
        const newAddresses = [...prev, currentAddress];
        handleAddressAdded(newAddresses.length);
        return newAddresses;
      });
      if (geocoderRefs.current['showing']) {
        geocoderRefs.current['showing'].clear();
      }
      setCurrentAddress(null);
    }
  };

  const handleRemoveAddress = (id: string) => {
    setAddresses(prev => {
      const newAddresses = prev.filter(addr => addr.id !== id);
      const nonUserLocations = newAddresses.length - (newAddresses.some(addr => addr.id === 'user-location') ? 1 : 0);
      
      // Hide the optimize route tooltip if we drop below 2 properties
      if (nonUserLocations < 2) {
        setShowTour(false);
        setCurrentStep(1); // Go back to property input step
      }
      
      return newAddresses;
    });
  };

  const handleOptimizeRoute = async () => {
    setIsLoading(true);
    setShowTour(false);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsLoading(false);
    onOptimizeRoute();
  };

  useEffect(() => {
    onAddressesChange(addresses);
  }, [addresses, onAddressesChange]);

  const renderTourTooltip = () => {
    if (!showTour || currentStep >= tourSteps.length) return null;
    
    const step = tourSteps[currentStep];
    const targetElement = document.querySelector(step.target);
    if (!targetElement) return null;

    // For the last step, position in center of screen
    const isFinalStep = currentStep === 2;
    const position = isFinalStep ? 'center' : (targetElement.getBoundingClientRect().top > window.innerHeight / 2 ? 'top' : 'bottom');
    
    return (
      <>
        <div 
          className="tour-overlay fixed inset-0 bg-black/30 backdrop-blur-sm"
          onClick={() => {
            setShowTour(false);
            if (currentStep === 0) {
              localStorage.setItem('hasSeenTour', 'true');
            }
          }}
        />
        <div 
          className="tour-tooltip fixed"
          data-position={position}
          style={isFinalStep ? {
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1001
          } : {
            top: position === 'bottom' ? `${targetElement.getBoundingClientRect().bottom + 10}px` : 'auto',
            bottom: position === 'top' ? `${window.innerHeight - targetElement.getBoundingClientRect().top + 10}px` : 'auto',
            left: `${targetElement.getBoundingClientRect().left + (targetElement.getBoundingClientRect().width / 2)}px`,
            transform: 'translateX(-50%)',
            zIndex: 1001
          }}
        >
          <div className="tour-tooltip-content">
            {step.content}
          </div>
          <div className="tour-tooltip-buttons">
            <button
              onClick={() => {
                setShowTour(false);
                if (currentStep === 0) {
                  localStorage.setItem('hasSeenTour', 'true');
                }
              }}
              className="tour-tooltip-button secondary"
            >
              Skip Tour
            </button>
            <button
              onClick={() => {
                setShowTour(false);
                if (currentStep === 0) {
                  localStorage.setItem('hasSeenTour', 'true');
                }
              }}
              className="tour-tooltip-button primary"
            >
              {currentStep < tourSteps.length - 1 ? 'Okay' : 'Finish'}
            </button>
          </div>
        </div>
      </>
    );
  };

  const handleGetCurrentLocation = () => {
    setIsGettingLocation(true);
    setShowTour(false);
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${mapboxgl.accessToken}`
          );
          const data = await response.json();
          
          if (!data.features || data.features.length === 0) {
            console.error('No location data found');
            setIsGettingLocation(false);
            return;
          }

          const placeName = data.features[0].place_name;
          console.log('Current location set:', placeName);
          
          setAddresses(prev => {
            const newAddresses = prev.filter(addr => addr.id !== 'user-location');
            const userLocation = {
              id: 'user-location',
              text: placeName,
              coordinates: [longitude, latitude] as [number, number],
            };
            return [userLocation, ...newAddresses];
          });
          
          setHasUserLocation(true);
          setLocationPulse(false);
          setLocationActive(true);
          setUserLocationText(placeName);
          handleLocationAdded();
        } catch (error) {
          console.error('Error reverse geocoding:', error);
        } finally {
          setIsGettingLocation(false);
        }
      }, (error) => {
        console.error('Error getting location:', error);
        setIsGettingLocation(false);
      }, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      });
    } else {
      console.error('Geolocation is not supported by this browser.');
      setIsGettingLocation(false);
    }
  };

  // Check if user has set their location
  const hasSetLocation = addresses.some(addr => addr.id === 'user-location');

  return (
    <div className="relative">
      <LoadingOverlay active={isLoading} text="Optimizing your route..." spinner={true}/>
      <div className="space-y-4">
        {renderTourTooltip()}
        
        <div className="location-input-section">
          <div className="flex items-center gap-20">
            <div className="flex-1 relative">
              <div
                ref={(el) => {
                  if (el) containerRefs.current['user-location'] = el;
                }}
                className="geocoder-container"
              />
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleGetCurrentLocation}
                disabled={isGettingLocation}
                className={`location-button flex items-center justify-center text-white ${locationPulse ? 'pulse' : ''} ${locationActive ? 'active' : ''}`}
                title="Use current location"
              >
                {locationActive ? (
                  <svg className="w-5 h-5 check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                ) : isGettingLocation ? (
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {userLocationText && (
          <div className="mb-4 text-center text-green-400 text-base font-medium flex items-center justify-center gap-2">
            <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            <span>Current Location: {userLocationText}</span>
          </div>
        )}

        {hasUserLocation && (
          <div className="showing-address-input">
            <div className="flex items-center gap-20">
              <div className="flex-1 relative">
                <div
                  ref={(el) => {
                    if (el) containerRefs.current['showing'] = el;
                  }}
                  className="geocoder-container"
                />
              </div>
              <div className="flex-shrink-0">
                <button
                  className={`add-address-button ${currentAddress ? 'bg-green-500 hover:bg-green-600 pulse' : 'bg-gray-600'} transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
                  onClick={handleAddAddress}
                  disabled={!currentAddress}
                  title="Add address to list"
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {addresses.filter(address => address.id !== 'user-location').map((address, index) => (
            <div
              key={address.id}
              className="flex items-center justify-between bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full text-white font-semibold">
                  {index + 1}
                </div>
                <span className="flex-1 text-gray-200">{address.text}</span>
              </div>
              <button
                onClick={() => handleRemoveAddress(address.id)}
                className="ml-3 text-red-400 hover:text-red-300 transition-colors duration-200"
                title="Remove address"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {(() => {
          const nonUserLocations = addresses.length - (addresses.some(addr => addr.id === 'user-location') ? 1 : 0);
          return nonUserLocations >= 2 ? (
            <div className="flex flex-col items-center gap-2">
              <p className="text-gray-300 text-sm">Click optimize route after you are done adding properties</p>
              <button
                className="optimize-route-button px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold transition-all duration-200 transform hover:scale-105 pulse"
                onClick={handleOptimizeRoute}
              >
                Optimize Route
              </button>
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

export default AddressForm; 
//7 Ocean View Drive, Dorchester, Massachusetts 02125, United States